import React, { useMemo, useRef } from 'react';
import { Box, Tooltip } from '@mui/material';
import { FaSync, FaDownload, FaFileExport } from 'react-icons/fa';
import { HeaderButton } from '../styles';
import { exportImportData, exportTemplate } from '../../../utils/csvExport';
import FileUploadButton from './FileUploadButton';

const ImportToolbar = ({ 
  config, 
  data = [], 
  loading = false, 
  validating = false,
  onRevalidate,
  onImport,
  onFileProcess,
  showAlert
}) => {
  const fileInputRef = useRef(null);
  const stats = useMemo(() => {
    return config.helpers.getStatusCounts(data);
  }, [data, config]);

  const getImportTooltip = () => {
    if (stats.invalid > 0) {
      return 'Fix all validation errors before importing';
    }
    if (stats.importable === 0) {
      // Use custom tooltip logic from config if available
      if (config.helpers?.getImportTooltip) {
        return config.helpers.getImportTooltip(stats, config);
      }
      
      // Default generic message
      return `No ${config.entityNamePlural.toLowerCase()} ready to import`;
    }
    return '';
  };

  const getImportButtonText = () => {
    const count = stats.importable;
    const entityName = count === 1 ? config.entityName : config.entityNamePlural;
    return `Import ${count} ${entityName}`;
  };

  const handleExportData = () => {
    try {
      exportImportData(data, config);
      showAlert?.(`Exported ${data.length} ${config.entityNamePlural.toLowerCase()}`, 'success');
    } catch (error) {
      showAlert?.(error.message, 'error');
    }
  };

  const handleExportTemplate = () => {
    try {
      exportTemplate(config);
      showAlert?.(`Downloaded ${config.entityName.toLowerCase()} template`, 'success');
    } catch (error) {
      showAlert?.(error.message, 'error');
    }
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: 1, 
      padding: '12px 16px', 
      borderBottom: data.length > 0 ? '1px solid #E5E7EB' : 'none'
    }}>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Tooltip title="Download CSV template for this import type">
          <HeaderButton
            onClick={handleExportTemplate}
            disabled={loading || validating}
          >
            <FaFileExport style={{ marginRight: '4px' }} />
            Template
          </HeaderButton>
        </Tooltip>

        {data.length > 0 && (
          <Tooltip title="Export current data as CSV">
            <HeaderButton
              onClick={handleExportData}
              disabled={loading || validating}
            >
              <FaDownload style={{ marginRight: '4px' }} />
              Export Data
            </HeaderButton>
          </Tooltip>
        )}

        <FileUploadButton
          onFileProcess={onFileProcess}
          fileInputRef={fileInputRef}
          disabled={loading || validating}
        />
      </Box>

      {data.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <HeaderButton
            onClick={onRevalidate}
            disabled={validating}
          >
            <FaSync style={{ marginRight: '4px' }} /> 
            Revalidate{stats.pending > 0 ? ' (Required)' : ''}
          </HeaderButton>

          <Tooltip title={getImportTooltip()}>
            <span>
              <HeaderButton
                onClick={onImport}
                disabled={loading || stats.invalid > 0 || stats.importable === 0}
              >
                {getImportButtonText()}
              </HeaderButton>
            </span>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
};

export default ImportToolbar; 